import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  Modal,
  Typography,
  Fade,
  Button,
  CircularProgress,
  Snackbar,
  InputAdornment,
  Grid,
  MenuItem,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { Alert } from '@material-ui/lab'
import Popconfirm from 'components/confirm/Popconfirm'
import TextField from 'components/inputs/TextField'
import { callPutApiWithAuth } from 'utils/api'
import causesData from 'testdata/causesData'
import { getFrequencySubscriptionList, getFrequencyKeyByValue, frequency } from 'config/var'

export default function EditSubscriptionModal({ data, closeEdition, afterSuccessUpdate }) {
  const classes = useStyles()
  const [isUpdating, setIsUpdating] = useState(false)
  const [updatingAnchor, setUpdatingAnchor] = useState(null)
  const [error, setError] = useState({
    open: false,
    message: ''
  })
  const [frequencyOptions, setFrequencyOptions] = useState(getFrequencySubscriptionList())
  const [causesListOptions, setCausesListOptions] = useState(undefined)
  const [formData, setFormData] = useState([{
    product_id: '',
    amount: '',
    cycle: '',
  }])

  const updateSubscription = () => {
    setIsUpdating(true)
    callPutApiWithAuth(`subscriptions/${data.id}/update`, formData, onSuccessUpdateSubscription, onFailedUpdateSubscription)
  }

  const onSuccessUpdateSubscription = (data) => {
    setIsUpdating(false)
    closeEdition()
    afterSuccessUpdate('Your subscription has been successfully updated.')
  }

  const onFailedUpdateSubscription = (err) => {
    setIsUpdating(false)
    setError({
      open: true,
      message: err.message
    })
  }

  useEffect(() => {
    let causesListOptions_ = []
    if (causesData.length > 0) {
      for (let i = 0; i < causesData.length; i ++) {
        causesListOptions_ = [
          ...causesListOptions_,
          {
            value: causesData[i].id,
            key: i + 1,
            label: causesData[i].shortTitle,
          },
        ]
      }
    }
    setCausesListOptions(causesListOptions_)
  }, [causesData])

  useEffect(() => {
    if (!data) return
    setFormData({
      product_id: (data ? data.details[0].product_id : ''),
      amount: (data ? data.details[0].amount : ''),
      cycle: (data ? data.cycle : ''),
    })
  }, [data])

  const handleChange = (field, value) => {
    if (field == 'amount') {
      let greaterThanZeroRegex = /^\d*\.?\d*$/;
      if (!value.match(greaterThanZeroRegex)) {
        setError({
          open: true,
          message: 'Amount should be greater than $0.00',
        })
        return
      }
      setFormData({ ...formData, amount: value })
    } else if (field == 'frequency') {
      setFormData({ ...formData, cycle: value })
    }
  }

  return (
    formData.product_id != '' ?
      <Modal open={data} onClose={closeEdition} style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <Fade in={data}>
          <div className={classes.modal}>
            <div className={classes.modalHeader}>
              <Typography component={'h2'}>Edit Donation</Typography>
            </div>
            <div className={classes.modalContent}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12} lg={12} xl={12} className={classes.causeField}>
                  <Typography component={'h2'}>Cause</Typography>
                  <TextField
                    select
                    variant="outlined"
                    className={`${classes.inputField} ${classes.causesInput}`}
                    value={formData.product_id}
                    disabled
                  >
                    {causesListOptions && causesListOptions.map((causesListOption) => (
                      <MenuItem key={causesListOption.key} value={causesListOption.value}>
                        {causesListOption.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6} lg={6} xl={6} className={classes.amountField}>
                  <Typography component={'h2'}>Amount</Typography>
                  <TextField
                    variant="outlined"
                    startAdornment={<InputAdornment position="start">$</InputAdornment>}
                    className={`${classes.inputField} ${classes.amountInput}`}
                    value={formData.amount}
                    onChange={(e) => handleChange('amount', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={6} xl={6} className={classes.frequencyField}>
                  <Typography component={'h2'}>Frequency</Typography>
                  <TextField
                    select
                    variant="outlined"
                    className={`${classes.inputField} ${classes.frequencyInput}`}
                    value={formData.cycle}
                    onChange={(e) => handleChange('frequency', e.target.value)}
                  >
                    {frequencyOptions && frequencyOptions.map((frequencyOption) => (
                      <MenuItem key={frequencyOption.key} value={frequencyOption.value}>
                        {frequencyOption.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </div>
            <div className={classes.modalFooter}>
              <Button
                variant='contained'
                color='primary'
                onClick={(e) => setUpdatingAnchor(e.currentTarget)}
                disabled={isUpdating}
                className={classes.updateButton}
              >
                {isUpdating && (
                  <CircularProgress
                    color={'primary'}
                    size={24}
                  />
                )}Update Donation
              </Button>
              <Popconfirm
                className={classes.editSubscriptionConfirmModal}
                anchorEl={updatingAnchor}
                onConfirm={() => {
                  updateSubscription()
                  setUpdatingAnchor(null)
                }}
                onCancel={() => setUpdatingAnchor(null)}
              />
            </div>
            <Snackbar
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              open={error.open}
              autoHideDuration={2000}
              onClose={(e, r) => {
                if (r == 'timeout') setError({
                  open: false,
                  message: ''
                })
              }}
            >
              <Alert
                severity='error'
                variant='filled'
                onClose={() => setError({
                  open: false,
                  message: ''
                })}
              >
                {error.message}
              </Alert>
            </Snackbar>
          </div>
        </Fade>
      </Modal>
      : ''
  )
}

const useStyles = makeStyles((theme) => ({
  modal: {
    width: 500,
    height: 265,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 24,
    left: 'auto !important',
    top: 'auto !important',
    position: 'fixed',
    '& h2': {
      fontFamily: 'Proxima Nova, sans-serif !important',
    },
    '@media (max-width: 960px)': {
      height: 350,
    },
    '@media (max-width: 600px)': {
      width: 265,
    },
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    '& h2': {
      fontSize: 14,
    },
  },
  modalHeader: {
    paddingBottom: 24,
    '& h2': {
      fontSize: 20,
    }
  },
  modalFooter: {
    marginTop: 24,
    textAlign: 'center',
  },
  updateButton: {
    width: 200,
    backgroundColor: `${theme.palette.primary.primaryColor} !important`,
    color: '#FFFFFF !important',
  },
  inputField: {
    width: '100%',
  },
  editSubscriptionConfirmModal: {
    '& .MuiPaper-root': {
      width: 200,
    },
    '& button': {
      width: '50%',
    }
  },
}))
